import { isFacilitySubscribeProtectPlus } from '@/entities/facility';
import dayjs from 'dayjs';
import _ from 'lodash';
import { t } from 'ttag';
import { NOTIFICATION_TYPES } from '../../config/notificationCenter';
import type {
  ClinicNotificationItem,
  NotificationInputItem,
  NotificationItem,
} from './notificationCenter.types';

/**
 * Expands the artifact studies list of a notification. Looks the element up by id and adds the show class when it exists.
 *
 * @param id - Element id of the artifact studies list.
 * @returns Nothing.
 */
const handleViewMoreArtifactStudiesClick = (id: string) => {
  const el = document.getElementById(id);
  if (el) {
    el.classList.add('--show');
  }
};

/**
 * Collapses the expanded artifact studies list of a notification. Looks the element up by id and removes the show class when it exists.
 *
 * @param id - Element id of the artifact studies list.
 * @returns Nothing.
 */
const handleViewLessArtifactStudiesClick = (id: string) => {
  const el = document.getElementById(id);
  if (el) {
    el.classList.remove('--show');
  }
};

/**
 * Renders the date heading of a notification group. Formats the date as MM/DD/YYYY and appends a Today suffix when it is zero days away from now.
 *
 * @param notificationDate - Notification creation date to format.
 * @returns The formatted date, suffixed with Today for today's notifications.
 */
const notificationDateFormatter = (notificationDate: string) => {
  const dateDiff = dayjs().diff(dayjs(notificationDate), 'day');
  const date = dayjs(notificationDate).format('MM/DD/YYYY');
  if (dateDiff === 0) {
    return t`${date} - Today`;
  }
  return date;
};

/**
 * Maps one raw notification record onto the shape the notification centre renders, per notification type. Switches on the type to pick maintenance device count, artifact or pending-upload study counts, program subscription state and date, RMA ticket and rma id, or the flagged-studies setting, suppressing the id for seen items and returning an empty object for zero counts and unknown types.
 *
 * @param item - Raw notification record to convert.
 * @returns The formatted notification, or an empty object when nothing should be shown.
 */
const formatNotificationItem = (item: NotificationInputItem): NotificationItem | undefined => {
  switch (item.type) {
    case NOTIFICATION_TYPES.MAINTENANCE_DEVICE_COUNT: {
      return {
        id: item.isSeen ? undefined : item.id,
        deviceCount: item.info?.deviceCount,
        createdAt: item.createdAt,
        isSeen: item.isSeen,
        facilityName: item?.facility?.name,
        facilityId: item?.facility?.id,
        isSubscribeProtectPlus: isFacilitySubscribeProtectPlus(item?.facility?.programs),
        type: item.type,
      };
    }
    case NOTIFICATION_TYPES.NEW_ARTIFACT_STUDIES: {
      const artifactStudies = !_.isEmpty(item.artifactStudies)
        ? item.artifactStudies
        : item?.info?.artifactStudies || [];
      const studyCount = item.artifactStudiesCount || artifactStudies?.length || 0;
      if (studyCount > 0) {
        return {
          id: item.isSeen ? undefined : item.id,
          createdAt: item.createdAt,
          isSeen: item.isSeen,
          facilityName: item.facility?.name,
          type: item.type,
          studyCount,
        };
      }
      return {};
    }
    case NOTIFICATION_TYPES.PENDING_UPLOAD_FLAGGED_STUDIES: {
      const studies = !_.isEmpty(item.studies) ? item.studies : item?.info?.studies || [];
      const studyCount = item.studiesCount || studies?.length || 0;
      if (studyCount > 0) {
        return {
          id: item.isSeen ? undefined : item.id,
          createdAt: item.createdAt,
          studyCount,
          isSeen: item.isSeen,
          facilityName: item.facility?.name,
          type: item.type,
        };
      }
      return {};
    }
    case NOTIFICATION_TYPES.PROGRAM_SUBSCRIPTION: {
      return {
        id: item.isSeen ? undefined : item.id,
        isProgramSubscribed: item.info?.isEnabled,
        toggleSubscriptionDate: item.info?.date,
        programName: item.info?.programName,
        createdAt: item.createdAt,
        isSeen: item.isSeen,
        facilityName: item?.facility?.name,
        type: item.type,
      };
    }
    case NOTIFICATION_TYPES.SHIPPING_LABEL_CREATED:
    case NOTIFICATION_TYPES.REMINDER_CREATE_SHIPMENT: {
      return {
        id: item.isSeen ? undefined : item.id,
        createdAt: item.createdAt,
        rmaTicket: item.info?.rmaTicket?.id || item.info?.rmaTicket,
        rmaId: item.info?.rmaTicket?.rmaId || item.rmaTicket?.rmaId,
        isSeen: item.isSeen,
        facilityName: item?.facility?.name,
        type: item.type,
      };
    }
    case NOTIFICATION_TYPES.FACILITY_SETTING_UPDATED: {
      return {
        id: item.isSeen ? undefined : item.id,
        createdAt: item.createdAt,
        canViewFlaggedStudies: item?.info?.canViewFlaggedStudies,
        isSeen: item.isSeen,
        facilityName: item?.facility?.name,
        type: item.type,
      };
    }
    case NOTIFICATION_TYPES.REMINDER_DEVICE_RETURN_7_DAYS:
    case NOTIFICATION_TYPES.REMINDER_DEVICE_RETURN_21_DAYS:
    case NOTIFICATION_TYPES.REMINDER_DEVICE_RETURN_26_DAYS:
    case NOTIFICATION_TYPES.CREATE_LABEL_RMA_REPLACEMENT: {
      return {
        id: item.isSeen ? undefined : item.id,
        createdAt: item.createdAt,
        rmaTicket: item.info?.rmaTicket?.id || item.info?.rmaTicket,
        rmaId: item.info?.rmaTicket?.rmaId || item.rmaTicket?.rmaId,
        isSeen: item.isSeen,
        facilityName: item?.facility?.name,
        type: item.type,
      };
    }
    default: {
      return {};
    }
  }
};

/**
 * Groups notifications into day buckets for the notification centre list. Orders them newest first, derives a local day key per item, creates the bucket the first time a day is seen, and appends each item's formatted payload while dropping the ones that format to nothing.
 *
 * @param notifications - Raw notification records to group.
 * @returns Day buckets, each holding its ISO date, day key and formatted notifications.
 */
const formatNotificationList = (notifications: NotificationInputItem[]) => {
  const output: {
    date: string;
    dateString: string;
    data: NotificationItem[];
  }[] = [];
  const orderedInput = _.orderBy(notifications, ['createdAt'], ['desc']);
  orderedInput.forEach((item) => {
    const currentStartDate = dayjs(item.createdAt).startOf('day'); // *: Local time
    const dateString = currentStartDate.format('YYYY-M-D');
    if (!_.find(output, (x) => x.dateString === dateString)) {
      output.push({
        date: dayjs(currentStartDate).toISOString(),
        data: [],
        dateString,
      });
    }

    /**
     * Finds the day-bucket in the output list whose dateString matches the current item's day. Locates it via Array.find over the accumulated output.
     *
     * @returns The matching day-bucket, or undefined.
     */
    const ele = output.find((x) => x.dateString === dateString);
    if (ele) {
      const formatData = formatNotificationItem(item);
      if (!_.isEmpty(formatData)) {
        ele.data.push(formatData);
      }
    }
  });

  return output;
};

/**
 * Flattens clinic notification records so the nested notification payload sits alongside the record's own fields. Returns an empty list for empty input, otherwise merges each item with its notification object.
 *
 * @param clinicNotifications - Clinic notification records, each wrapping a notification payload.
 * @returns The clinic notifications merged with their notification payloads.
 */
const formatClinicNotifications = (clinicNotifications: ClinicNotificationItem[] | undefined) => {
  if (_.isEmpty(clinicNotifications)) return [];
  return _.map(clinicNotifications, (item) => ({ ...item, ...item.notification }));
};

export {
  formatClinicNotifications,
  formatNotificationItem,
  formatNotificationList,
  handleViewLessArtifactStudiesClick,
  handleViewMoreArtifactStudiesClick,
  notificationDateFormatter,
};
